/**
 * GroundedClaimCard - Displays factual claims with their source citations
 * 
 * Design principles:
 * - Clear visual hierarchy: claim → source → excerpt
 * - Clickable sources to open artifacts
 * - Confidence indicator per claim
 * - Subtle but trustworthy appearance
 */

import React from 'react';
import type { GroundedClaim } from '../../../api/services/agent';
import { ConfidenceBadge } from './ConfidenceIndicator';

interface GroundedClaimCardProps {
  claim: GroundedClaim;
  onSourceClick?: (artifactId: string) => void;
  compact?: boolean;
}

export const GroundedClaimCard: React.FC<GroundedClaimCardProps> = ({
  claim,
  onSourceClick,
  compact = false,
}) => {
  const handleSourceClick = () => {
    if (onSourceClick) {
      // Normalize artifact ID (remove "artifact:" prefix if present)
      const normalizedId = claim.source_artifact_id.replace(/^artifact:/, '');
      onSourceClick(normalizedId);
    }
  };

  if (compact) {
    return (
      <div className="group flex items-start gap-2 py-1.5">
        {/* Source indicator */}
        <div
          className="flex-shrink-0 w-1 h-full min-h-[20px] rounded-full"
          style={{ backgroundColor: 'var(--color-primary-300)' }}
        />
        
        {/* Claim text */}
        <div className="flex-1 min-w-0">
          <p className="text-[13px] text-[color:var(--color-neutral-800)] leading-relaxed">
            {claim.claim}
          </p>
          
          {/* Source link */}
          <button
            type="button"
            onClick={handleSourceClick}
            className="mt-1 text-[11px] text-[color:var(--color-primary-600)] hover:text-[color:var(--color-primary-700)] hover:underline transition-colors"
          >
            📄 {claim.source_title}
          </button>
        </div>

        {/* Confidence */}
        <ConfidenceBadge confidence={claim.confidence} className="flex-shrink-0" />
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-[color:var(--color-primary-100)] bg-[color:var(--color-primary-25)] p-4 transition-all duration-200 hover:border-[color:var(--color-primary-200)] hover:shadow-sm"
    >
      {/* Header: Confidence badge */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-medium uppercase tracking-wider text-[color:var(--color-primary-500)]">
          Grounded Fact
        </span>
        <ConfidenceBadge confidence={claim.confidence} />
      </div>

      {/* Claim text */}
      <p className="text-[14px] font-medium text-[color:var(--color-neutral-900)] leading-relaxed mb-3">
        "{claim.claim}"
      </p>

      {/* Source section */}
      <div className="rounded-lg bg-white/60 p-3 border border-[color:var(--color-primary-100)]">
        <div className="flex items-center gap-2 mb-2">
          <svg
            className="w-4 h-4 text-[color:var(--color-primary-500)]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <button
            type="button"
            onClick={handleSourceClick}
            className="text-[13px] font-medium text-[color:var(--color-primary-600)] hover:text-[color:var(--color-primary-700)] hover:underline transition-colors"
          >
            {claim.source_title}
          </button>
        </div>
        
        {/* Source excerpt */}
        <p className="text-[12px] text-[color:var(--color-neutral-600)] leading-relaxed italic pl-6">
          "{claim.source_excerpt.length > 150 
            ? `${claim.source_excerpt.slice(0, 150)}...` 
            : claim.source_excerpt}"
        </p>
      </div>
    </div>
  );
};

/**
 * GroundedClaimsList - Render multiple grounded claims
 */
interface GroundedClaimsListProps {
  claims: GroundedClaim[];
  onSourceClick?: (artifactId: string) => void;
  compact?: boolean;
  maxVisible?: number;
}

export const GroundedClaimsList: React.FC<GroundedClaimsListProps> = ({
  claims,
  onSourceClick,
  compact = false,
  maxVisible = 3,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  
  if (!claims || claims.length === 0) {
    return null;
  }

  const visibleClaims = expanded ? claims : claims.slice(0, maxVisible);
  const hiddenCount = claims.length - maxVisible;

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-5 h-5 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'var(--color-primary-100)' }}
        >
          <svg
            className="w-3 h-3 text-[color:var(--color-primary-600)]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <span className="text-[12px] font-medium text-[color:var(--color-neutral-700)]">
          {claims.length} verified {claims.length === 1 ? 'fact' : 'facts'}
        </span>
      </div>

      {/* Claims list */}
      <div className={compact ? 'space-y-1' : 'space-y-3'}>
        {visibleClaims.map((claim, index) => (
          <GroundedClaimCard
            key={`${claim.source_artifact_id}-${index}`}
            claim={claim}
            onSourceClick={onSourceClick}
            compact={compact}
          />
        ))}
      </div>

      {/* Show more button */}
      {!expanded && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-2 text-[12px] text-[color:var(--color-primary-600)] hover:text-[color:var(--color-primary-700)] hover:underline transition-colors"
        >
          Show {hiddenCount} more {hiddenCount === 1 ? 'fact' : 'facts'}
        </button>
      )}
      
      {expanded && claims.length > maxVisible && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="mt-2 text-[12px] text-[color:var(--color-neutral-500)] hover:text-[color:var(--color-neutral-700)] hover:underline transition-colors"
        >
          Show less
        </button>
      )}
    </div>
  );
};

export default GroundedClaimCard;

